/**
 * Custom Domain — client helpers.
 * Firestore collection: custom_domains
 */

export const CUSTOM_DOMAIN_CREATE_ERRORS = {
  REQUIRED: "Hostname is required",
  INVALID_FORMAT: "Invalid hostname format",
  RESERVED: "Hostname uses a reserved suffix",
  ZONE_ID_REQUIRED: "Cloudflare Zone ID is required",
  ALREADY_TEAM: "Domain already exists for this team",
  ALREADY_GLOBAL: "DOMAIN_IN_USE_ELSEWHERE",
} as const;

export type CustomDomainStatus =
  | "REQUESTED"
  | "PROVISIONING_PROVIDER"
  | "AWAITING_DNS_CONFIG"
  | "DNS_PROPAGATING"
  | "SSL_PROVISIONING"
  | "ACTIVE"
  | "FAILED"
  | "DELETING"
  | "DELETED"
  /** ค่าจาก HTTP check-status / backend custom-domains repo */
  | "pending_dns"
  | "validating_ssl"
  | "active"
  | "failed"
  | "revalidation_warning"
  | "revalidation_failed"
  | "suspended"
  | "removing";

export type DnsProviderId = "cloudflare" | "manual" | "route53";

export type CustomerSetupPhase = "pending_dns" | "validating_ssl" | "active" | "other";

export type UiStep = "pending" | "verifying" | "active" | "failed";

export interface DnsRequiredRecord {
  type: string;
  name: string;
  content: string;
  ttl?: number;
  proxied?: boolean;
}

/** Fallback CNAME target (Cloudflare for SaaS) — see docs/custom-domains/custom-domains-design.md */
export const CUSTOM_DOMAIN_CNAME_TARGET_DEFAULT = "cs.myculture.ai";

/**
 * Hostname customers CNAME to — from VUE_APP_CUSTOM_DOMAIN_CNAME_TARGET or default above.
 */
export function getCustomDomainCnameTarget(): string {
  const fromEnv = String(
    process.env.VUE_APP_CUSTOM_DOMAIN_CNAME_TARGET || ""
  ).trim();
  return fromEnv || CUSTOM_DOMAIN_CNAME_TARGET_DEFAULT;
}

/**
 * Step 2 wizard: CNAME (customer host → fallback origin) first, then API records (e.g. DCV TXT).
 * Strips a duplicate CNAME row from the API if it matches the same hostname.
 */
export function mergeStep2DnsRecords(
  hostname: string,
  apiRecords: DnsRequiredRecord[]
): DnsRequiredRecord[] {
  const h = normalizeHostname(hostname);
  if (!h) return apiRecords;
  const cnameRow: DnsRequiredRecord = {
    type: "CNAME",
    name: h,
    content: getCustomDomainCnameTarget(),
  };
  const filtered = apiRecords.filter((r) => {
    const t = String(r.type || "").toUpperCase();
    const n = normalizeHostname(r.name);
    if (t === "CNAME" && n === h) return false;
    return true;
  });
  return [cnameRow, ...filtered];
}

/** รูปแบบที่ backend บางเส้นใช้แทน requiredRecords[] (เช่น DCV TXT) */
export interface DcvDnsRecord {
  name?: string;
  type?: string;
  value?: string;
  content?: string;
}

/**
 * แปลงแถวจาก Firestore/API ให้เป็น DnsRequiredRecord (รองรับ key `value` แทน `content`)
 */
export function normalizeDnsRequiredRow(raw: unknown): DnsRequiredRecord | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const type = String(o.type ?? "").trim();
  const name = String(o.name ?? "").trim();
  const content = String(o.content ?? o.value ?? "").trim();
  if (!type && !name && !content) return null;
  const row: DnsRequiredRecord = {
    type: type || "TXT",
    name,
    content,
  };
  if (typeof o.ttl === "number") row.ttl = o.ttl;
  if (typeof o.proxied === "boolean") row.proxied = o.proxied;
  return row;
}

/** Denormalized for public hostname resolve (filled by backend when ACTIVE). */
export interface CustomDomainPublicBranding {
  companyName?: string;
  color?: string;
  companyLogoUrl?: string;
}

function publicBrandingTrim(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** True if any public branding field is missing (login/signup may merge from teams/{teamId}). */
export function customDomainPublicBrandingNeedsTeamFallback(
  pb: CustomDomainPublicBranding | null | undefined
): boolean {
  if (!pb) return true;
  return (
    !publicBrandingTrim(pb.companyLogoUrl) ||
    !publicBrandingTrim(pb.companyName) ||
    !publicBrandingTrim(pb.color)
  );
}

/**
 * Fill empty branding keys from team doc (`companyLogoUrl`, `companyName`, `color`).
 * Values on custom_domains.publicBranding win when non-empty.
 */
export function mergePublicBrandingWithTeamFields(
  fromDomain: CustomDomainPublicBranding | null | undefined,
  teamData: Record<string, unknown> | null | undefined
): CustomDomainPublicBranding | undefined {
  const out: CustomDomainPublicBranding = { ...(fromDomain || {}) };
  const t =
    teamData && typeof teamData === "object" && !Array.isArray(teamData)
      ? teamData
      : null;
  if (t) {
    if (!publicBrandingTrim(out.companyLogoUrl) && publicBrandingTrim(t.companyLogoUrl)) {
      out.companyLogoUrl = String(t.companyLogoUrl).trim();
    }
    if (!publicBrandingTrim(out.companyName) && publicBrandingTrim(t.companyName)) {
      out.companyName = String(t.companyName).trim();
    }
    if (!publicBrandingTrim(out.color) && publicBrandingTrim(t.color)) {
      out.color = String(t.color).trim();
    }
  }
  const hasAnything =
    !!publicBrandingTrim(out.companyLogoUrl) ||
    !!publicBrandingTrim(out.companyName) ||
    !!publicBrandingTrim(out.color);
  return hasAnything ? out : undefined;
}

export interface CustomDomainDoc {
  _id?: string;
  teamId: string;
  hostname: string;
  status: CustomDomainStatus;
  currentStep?: string;
  statusReason?: string;
  hostingProvider?: string;
  dnsProvider?: DnsProviderId;
  cloudflareZoneId?: string;
  publicBranding?: CustomDomainPublicBranding;
  providerRefs?: {
    firebaseDomainName?: string;
    cloudflareRecordIds?: string[];
    lastHostingPayload?: {
      domainName?: string;
      status?: string;
      provisioning?: {
        certStatus?: string;
        dnsStatus?: string;
        expectedIps?: string[];
      };
      site?: string;
      updateTime?: string;
    };
  };
  /** DCV / DNS จาก API บางชุดเก็บระดับบนสุด */
  dcvRecord?: DcvDnsRecord | DcvDnsRecord[];
  verification?: {
    requiredRecords?: DnsRequiredRecord[];
    /** แบบเดียวกับ requiredRecords แต่ใช้ { name, type, value } */
    dcvRecord?: DcvDnsRecord | DcvDnsRecord[];
    verifiedAt?: unknown;
    /** Backend worker should regenerate DCV/TXT when this is touched. */
    dcvRegenerateRequestedAt?: unknown;
  };
  ssl?: {
    sslMode?: "managed_auto";
    certificateStatus?: string;
    issuedAt?: unknown;
    expiresAt?: unknown;
    renewCheckAt?: unknown;
  };
  retry?: {
    attempt: number;
    nextRetryAt?: unknown;
    lastError?: string;
  };
  deleted?: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
  lastVerifiedAt?: unknown;
}

/**
 * เรคคอร์ดที่ UI ต้องแสดง: ใช้ verification.requiredRecords ก่อน ถ้าว่างหรือ normalize ไม่ได้
 * จะอ่าน verification.dcvRecord หรือ dcvRecord ระดับบนสุดของเอกสาร
 */
export function getRequiredRecordsForUi(
  domain: CustomDomainDoc | null | undefined
): DnsRequiredRecord[] {
  if (!domain) return [];
  const rawList = domain.verification?.requiredRecords;
  if (Array.isArray(rawList) && rawList.length > 0) {
    const mapped = rawList
      .map((r) => normalizeDnsRequiredRow(r))
      .filter((r): r is DnsRequiredRecord => r != null);
    if (mapped.length > 0) return mapped;
  }
  const dcvRaw =
    domain.verification?.dcvRecord ?? domain.dcvRecord ?? undefined;
  if (Array.isArray(dcvRaw)) {
    return dcvRaw
      .map((r) => normalizeDnsRequiredRow(r))
      .filter((r): r is DnsRequiredRecord => r != null);
  }
  const one = normalizeDnsRequiredRow(dcvRaw);
  return one ? [one] : [];
}

const HOSTNAME_RE =
  /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;

export function normalizeHostname(input: string): string {
  if (!input) return "";
  return String(input)
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "")
    .replace(/\s+/g, "");
}

export function isValidHostname(host: string): boolean {
  if (!host) return false;
  if (host.length > 253) return false;
  return HOSTNAME_RE.test(host);
}

/** ส่วนก่อน @ เท่านั้น — ไม่อนุญาตช่องว่างหรือ @ ในช่องกรอก */
export function isValidEmailLocalPart(local: string): boolean {
  const s = String(local ?? "").trim();
  if (!s || s.length > 64) return false;
  if (/[\s@]/.test(s)) return false;
  if (s.startsWith(".") || s.endsWith(".")) return false;
  if (/\.{2,}/.test(s)) return false;
  return /^[a-zA-Z0-9._%+-]+$/.test(s);
}

/** ประกอบที่อยู่จาก local + hostname portal (ใช้หลัง normalizeHostname) */
export function buildEmailFromLocalAndHostname(
  local: string,
  hostname: string
): string {
  const l = String(local ?? "").trim();
  const h = normalizeHostname(hostname);
  if (!l || !h) return "";
  return `${l}@${h}`;
}

function getReservedSuffixes(): string[] {
  const raw = process.env.VUE_APP_RESERVED_DOMAIN_SUFFIXES;
  if (!raw) return ["myculture.ai"];
  return String(raw)
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function isReservedHostname(host: string): boolean {
  if (!host) return false;
  const h = host.toLowerCase();
  const authDomain = process.env.VUE_APP_FIREBASE_AUTH_DOMAIN || "";
  if (authDomain) {
    const ad = authDomain.split(":")[0].toLowerCase();
    if (h === ad || h.endsWith("." + ad)) return true;
  }
  return getReservedSuffixes().some(
    (suffix) => h === suffix || h.endsWith("." + suffix)
  );
}

/** Maps backend status to customer copy: pending_dns | validating_ssl | active | other */
export function statusToCustomerPhase(
  status?: CustomDomainStatus | string
): CustomerSetupPhase {
  switch (status) {
    case "AWAITING_DNS_CONFIG":
    case "DNS_PROPAGATING":
    case "REQUESTED":
    case "PROVISIONING_PROVIDER":
    case "pending_dns":
      return "pending_dns";
    case "SSL_PROVISIONING":
    case "validating_ssl":
      return "validating_ssl";
    case "ACTIVE":
    case "active":
      return "active";
    default:
      return "other";
  }
}

export function statusToUiStep(status?: CustomDomainStatus | string): UiStep {
  switch (status) {
    case "ACTIVE":
    case "active":
      return "active";
    case "FAILED":
    case "failed":
    case "revalidation_failed":
      return "failed";
    case "AWAITING_DNS_CONFIG":
    case "DNS_PROPAGATING":
    case "SSL_PROVISIONING":
    case "validating_ssl":
    case "revalidation_warning":
      return "verifying";
    case "REQUESTED":
    case "PROVISIONING_PROVIDER":
    case "pending_dns":
    case "DELETING":
    case "removing":
    default:
      return "pending";
  }
}

export function statusBadgeLabelKey(
  status?: CustomDomainStatus | string
): string {
  switch (status) {
    case "REQUESTED":
      return "customDomain.status.REQUESTED";
    case "PROVISIONING_PROVIDER":
      return "customDomain.status.PROVISIONING_PROVIDER";
    case "AWAITING_DNS_CONFIG":
    case "pending_dns":
      return "customDomain.status.AWAITING_DNS_CONFIG";
    case "DNS_PROPAGATING":
      return "customDomain.status.DNS_PROPAGATING";
    case "SSL_PROVISIONING":
    case "validating_ssl":
      return "customDomain.status.SSL_PROVISIONING";
    case "ACTIVE":
    case "active":
      return "customDomain.status.ACTIVE";
    case "FAILED":
    case "failed":
    case "revalidation_failed":
      return "customDomain.status.FAILED";
    case "DELETING":
    case "removing":
      return "customDomain.status.DELETING";
    case "revalidation_warning":
      return "customDomain.status.SSL_PROVISIONING";
    case "suspended":
      return "customDomain.status.FAILED";
    default:
      return "customDomain.status.REQUESTED";
  }
}

export function diagnose(
  domain: CustomDomainDoc | null | undefined
): string {
  if (!domain) return "customDomain.diagnostics.Generic";

  const dns = domain.providerRefs?.lastHostingPayload?.provisioning?.dnsStatus;
  const cert = domain.providerRefs?.lastHostingPayload?.provisioning?.certStatus;
  const reason = domain.statusReason || "";
  const lastError = domain.retry?.lastError || "";

  const failed =
    domain.status === "FAILED" ||
    domain.status === "failed" ||
    domain.status === "revalidation_failed";
  if (failed && /dcv|txt|ssl|certificate/i.test(reason + lastError)) {
    return "customDomain.diagnostics.DcvFailed";
  }
  if (dns && !dns.toUpperCase().includes("MATCH")) {
    return "customDomain.diagnostics.CnameMismatch";
  }
  if (cert && /PENDING|PROPAGATING/i.test(cert)) {
    return "customDomain.diagnostics.CertPending";
  }
  if (/timeout/i.test(reason) || /timeout/i.test(lastError)) {
    return "customDomain.diagnostics.Timeout";
  }
  if (/hosting_status/i.test(reason) || lastError) {
    return "customDomain.diagnostics.NotPropagated";
  }
  return "customDomain.diagnostics.Generic";
}

/** Heuristic: stuck waiting for DNS for a long time (client-side UX hint). */
export function isLikelyDnsTimeout(domain: CustomDomainDoc | null): boolean {
  if (!domain) return false;
  const s = domain.status;
  if (
    s === "ACTIVE" ||
    s === "active" ||
    s === "FAILED" ||
    s === "failed" ||
    s === "revalidation_failed"
  ) {
    return false;
  }
  const phase = statusToCustomerPhase(domain.status);
  if (phase !== "pending_dns") return false;
  const ms = createdAtToMillis(domain.createdAt);
  if (!ms) return false;
  return Date.now() - ms > 60 * 60 * 1000;
}

function createdAtToMillis(value: unknown): number {
  if (value == null) return 0;
  if (typeof (value as { toMillis?: () => number }).toMillis === "function") {
    return (value as { toMillis: () => number }).toMillis();
  }
  const s = (value as { seconds?: number }).seconds;
  if (typeof s === "number") return s * 1000;
  return 0;
}

export function isProvisioningStep(domain: CustomDomainDoc | null): boolean {
  if (!domain) return false;
  const s = domain.status;
  return (
    s !== "ACTIVE" &&
    s !== "active" &&
    s !== "FAILED" &&
    s !== "failed" &&
    s !== "revalidation_failed" &&
    s !== "suspended" &&
    s !== "DELETED" &&
    s !== "DELETING" &&
    s !== "removing"
  );
}

/** myculture.com and *.myculture.com — canonical product hosts (no custom-domain binding). */
export function isPrimaryMycultureHostname(hostname: string): boolean {
  const h = normalizeHostname(hostname).split(":")[0];
  if (!h) return false;
  if (h === "myculture.ai") return true;
  return h.endsWith(".myculture.ai");
}

export function normalizedCustomDomainStatusValue(status: unknown): string {
  return String(status ?? "")
    .trim()
    .toLowerCase();
}

/** Status values that still allow traffic after hostname bootstrap (fail-closed otherwise). */
export function isServingCustomHostnameDocStatus(status: unknown): boolean {
  const x = normalizedCustomDomainStatusValue(status);
  return (
    x === "active" || x === "revalidation_warning" || String(status) === "ACTIVE"
  );
}

export function isSuspendedCustomHostnameDocStatus(status: unknown): boolean {
  return normalizedCustomDomainStatusValue(status) === "suspended";
}

/**
 * Resolve membership list: `teamIds` when non-empty, else single `teamId`.
 *
 * @deprecated For custom-host access control prefer checking `teams/{id}.members` (or a future
 * membership collection query); user profiles often omit full `teamIds` today.
 */
export function membershipTeamIdsFromUser(
  user: Record<string, unknown> | null | undefined
): string[] {
  if (!user) return [];
  const raw = user.teamIds;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((id) => String(id));
  }
  if (user.teamId != null && String(user.teamId).trim() !== "") {
    return [String(user.teamId)];
  }
  return [];
}

/** HTTPS origin for shared assessment links (`https://hostname`). Uses current window when hostname empty. */
export function buildCandidateShareOrigin(
  hostnameOrEmpty: string | null | undefined
): string {
  const raw = hostnameOrEmpty != null ? String(hostnameOrEmpty).trim() : "";
  const h = normalizeHostname(raw).split(":")[0];
  const proto =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  if (h) return `${proto}//${h}`;
  if (typeof window !== "undefined") return `${proto}//${window.location.host}`;
  return "https://";
}

/**
 * Logo from Firestore `custom_domains.publicBranding.companyLogoUrl`: HTTPS URL as-is;
 * otherwise treated as Firebase Storage path and resolved via getDownloadURL.
 */
export async function resolvePublicBrandingLogoDisplayUrl(
  raw: unknown
): Promise<string> {
  if (typeof raw !== "string") {
    return "";
  }
  const t = raw.trim();
  if (!t) {
    return "";
  }
  if (/^https?:\/\//i.test(t)) {
    return t;
  }
  try {
    const { getStorage, ref, getDownloadURL } = await import(
      "firebase/storage"
    );
    const url = await getDownloadURL(ref(getStorage(), t));
    return url;
  } catch {
    return "";
  }
}
